'use client'

import { Maximize2, Play, Volume2, VolumeX } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

export type SampleMedia = {
  src: string
  kind: 'image' | 'video'
  label: string
  poster?: string
}

type Side = 'before' | 'after'

const SAMPLE_SOUND_EVENT = 'soku:sample-video-sound'

/**
 * Both halves of one sample, each of which opens full-size.
 *
 * A translated ad is only evidence if the copy on it can be read, and at
 * three-cards-per-row the copy is the first thing to go. The thumbnails stay
 * small — the grid is a contact sheet, and it should scan like one — but every
 * cell opens into a dialog sized to the viewport, with a toggle between the
 * two sides so the check the visitor actually wants to make (did the product,
 * the layout and the type survive?) happens at full size and in one place.
 */
export function MediaWorkflowSamplePair({
  title,
  caption,
  before,
  after,
  cellClassName,
  captionClassName,
  arrow,
}: {
  title: string
  caption: string
  /**
   * Absent on prompt-in tools. A text-to-video page has no "before" — the
   * input is a sentence, not an asset — and pairing the output against a
   * still lifted out of itself would be a comparison that proves nothing.
   * When it is absent the cell renders alone, full width, with no arrow.
   */
  before?: SampleMedia
  after: SampleMedia
  /** Aspect + surface classes for the thumbnail cell. */
  cellClassName: string
  captionClassName: string
  /** Rendered between the two halves. */
  arrow: React.ReactNode
}) {
  const [openSide, setOpenSide] = useState<Side | null>(null)
  const [soundSide, setSoundSide] = useState<Side | null>(null)
  const playerId = useId()
  // Inline players are driven directly for responsive hover playback. Held by
  // ref so `openZoom` can stop them before the dialog starts a second copy;
  // whether a native player is playing is not React state to derive.
  const inlineRefs = useRef<Partial<Record<Side, HTMLVideoElement | null>>>({})
  const openZoom = (side: Side) => {
    for (const video of Object.values(inlineRefs.current)) video?.pause()
    setSoundSide(null)
    setOpenSide(side)
  }

  // A gallery is many independent cards, but the page must still behave like
  // one player: unmuting a second sample mutes the first instead of layering
  // two generated soundtracks. A small window event keeps that rule shared
  // without coupling every gallery to a page-level context.
  useEffect(() => {
    const muteForAnotherPlayer = (event: Event) => {
      if ((event as CustomEvent<string>).detail === playerId) return
      setSoundSide(null)
    }
    window.addEventListener(SAMPLE_SOUND_EVENT, muteForAnotherPlayer)
    return () =>
      window.removeEventListener(SAMPLE_SOUND_EVENT, muteForAnotherPlayer)
  }, [playerId])

  const playInline = (side: Side) => {
    void inlineRefs.current[side]?.play().catch(() => {})
  }

  const resetInline = (side: Side) => {
    const video = inlineRefs.current[side]
    if (!video) return
    video.pause()
    video.currentTime = 0
    video.muted = true
    setSoundSide(current => (current === side ? null : current))
  }

  const toggleSound = (side: Side) => {
    const next = soundSide === side ? null : side
    setSoundSide(next)
    if (next) {
      window.dispatchEvent(
        new CustomEvent<string>(SAMPLE_SOUND_EVENT, { detail: playerId }),
      )
      void inlineRefs.current[next]?.play().catch(() => {})
    }
  }
  const media = { before, after }
  // Paired with its media rather than looked up by key, so the optional
  // `before` narrows once here instead of at every use inside the cell.
  const sides: [Side, SampleMedia][] = before
    ? [
        ['before', before],
        ['after', after],
      ]
    : [['after', after]]
  const active = openSide ? (media[openSide] ?? null) : null

  return (
    <>
      {sides.map(([side, item], index) => (
        <div key={side} className="contents">
          {index === 1 ? arrow : null}
          <figure className="min-w-0">
            <div
              className={cn('group/cell relative', cellClassName)}
              onMouseEnter={() => {
                if (item.kind === 'video') playInline(side)
              }}
              onMouseLeave={() => {
                if (item.kind === 'video') resetInline(side)
              }}
            >
              {item.kind === 'video' ? (
                <>
                  <video
                    ref={element => {
                      inlineRefs.current[side] = element
                    }}
                    src={item.src}
                    poster={item.poster}
                    muted={soundSide !== side}
                    loop
                    playsInline
                    // A gallery of a dozen clips should download posters, not
                    // a dozen videos. Hovering starts the real file on demand.
                    preload={item.poster ? 'none' : 'metadata'}
                    className="size-full object-contain"
                  />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/10 transition-opacity group-hover/cell:opacity-0">
                    <span className="grid size-9 place-items-center rounded-full border border-white/20 bg-black/45 text-white backdrop-blur-sm">
                      <Play className="ml-0.5 size-3.5 fill-current" />
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label={
                      soundSide === side
                        ? `Mute ${item.label}`
                        : `Hear ${item.label}`
                    }
                    onClick={() => toggleSound(side)}
                    className="absolute right-1.5 bottom-1.5 z-10 rounded-full bg-black/60 p-1.5 text-white/90 transition-colors hover:bg-black/80"
                  >
                    {soundSide === side ? (
                      <Volume2 className="size-3.5" />
                    ) : (
                      <VolumeX className="size-3.5" />
                    )}
                  </button>
                </>
              ) : (
                // Gallery assets mix local generated samples with provider
                // output. A native img keeps both arbitrary ratios and
                // non-Next CDN hosts honest.
                <img
                  src={item.src}
                  alt={item.label}
                  loading="lazy"
                  onClick={() => openZoom(side)}
                  className="size-full cursor-zoom-in object-contain"
                />
              )}
              {/* A button rather than a click target on the whole cell: a
                  video's own controls own its surface, and stealing that
                  click would break play/scrub to add zoom. */}
              <button
                type="button"
                aria-label={`View ${item.label} full size`}
                onClick={() => openZoom(side)}
                className="absolute top-1.5 right-1.5 rounded-full bg-black/55 p-1.5 text-white opacity-0 transition-opacity group-hover/cell:opacity-100 focus-visible:opacity-100"
              >
                <Maximize2 className="size-3.5" />
              </button>
            </div>
            <figcaption className={captionClassName}>{item.label}</figcaption>
          </figure>
        </div>
      ))}

      <Dialog
        open={openSide !== null}
        onOpenChange={open => {
          if (!open) setOpenSide(null)
        }}
      >
        <DialogContent className="max-h-[92dvh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto border-white/10 bg-stone-950 p-4 text-white sm:max-w-5xl sm:p-6">
          <DialogTitle className="pr-8 font-sans text-xl leading-tight">
            {title}
          </DialogTitle>
          {/* Hidden when there is only one side: a toggle with a single
              option is a control that cannot do anything. */}
          {sides.length > 1 ? (
            <div className="inline-flex gap-1 rounded-full border border-white/10 bg-white/5 p-1 text-xs font-semibold">
              {sides.map(([side, item]) => (
                <button
                  key={side}
                  type="button"
                  onClick={() => setOpenSide(side)}
                  className={cn(
                    'rounded-full px-3 py-1.5 transition-colors',
                    openSide === side
                      ? 'bg-orange-500 text-stone-950'
                      : 'text-white/50 hover:bg-white/5 hover:text-white',
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
          {active ? (
            <div className="flex max-h-[65dvh] items-center justify-center overflow-hidden rounded-xl bg-black/40">
              {active.kind === 'video' ? (
                <video
                  key={active.src}
                  src={active.src}
                  poster={active.poster}
                  controls
                  autoPlay
                  playsInline
                  className="max-h-[65dvh] w-auto max-w-full object-contain"
                />
              ) : (
                <img
                  src={active.src}
                  alt={active.label}
                  className="max-h-[65dvh] w-auto max-w-full object-contain"
                />
              )}
            </div>
          ) : null}
          <DialogDescription className="text-sm leading-6 text-white/50">
            {caption}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  )
}
